/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-08-06 08:34:34
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, getFormValues, change } from 'redux-form';
import {
  FormLayout,
  TextStyle,
  RangeSlider,
  SkeletonDisplayText,
  Link,
  Button,
  Stack
} from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import RenderColorPickerHexPopover from 'src/components/views/common/render-color-picker-hex-popover';
import { TEXT_TRANSFORM } from 'src/constants/theme-settings';
import { normalizeNumberRangeSlider } from 'src/helpers/form-validation-helper';
import { minValue } from 'src/helpers/form-validation-helper';
import { buildOptionList } from 'src/helpers';
import { URL } from 'src/constants/links';

type Props = WithI18nProps;

type State = {
  showProductTitleSection: boolean,
  showProductVendorSection: boolean,
  showProductPriceSection: boolean,
  showProductDescriptionSection: boolean,
  textTransform: Array<{ value: string, label: string }>,
  productTitleFontStyle: Array<{ value: string, label: string }>,
  productVendorFontStyle: Array<{ value: string, label: string }>,
  productPriceFontStyle: Array<{ value: string, label: string }>,
  productDesFontStyle: Array<{ value: string, label: string }>
};

class DetailTab extends Component<Props, State> {
  state = {
    showProductTitleSection: true,
    showProductVendorSection: true,
    showProductPriceSection: true,
    showProductDescriptionSection: true,
    textTransform: [],
    productTitleFontStyle: [],
    productVendorFontStyle: [],
    productPriceFontStyle: [],
    productDesFontStyle: []
  };

  componentDidMount() {
    const { i18n } = this.props;

    // Init font style based on saved value
    this.updateFontStyle('productTitleFontStyle', this.props.productTitleFont, true);
    this.updateFontStyle('productVendorFontStyle', this.props.productVendorFont, true);
    this.updateFontStyle('productPriceFontStyle', this.props.productPriceFont, true);
    this.updateFontStyle('productDesFontStyle', this.props.productDesFont, true);

    const textTransform = buildOptionList(TEXT_TRANSFORM, i18n);
    this.setState({ textTransform });
  }

  componentWillReceiveProps(nextProps) {
    // Loaded theme settings and font
    if (this.props.loading && !nextProps.loading) {
      this.updateFontStyle('productTitleFontStyle', nextProps.productTitleFont, true);
      this.updateFontStyle('productVendorFontStyle', nextProps.productVendorFont, true);
      this.updateFontStyle('productPriceFontStyle', nextProps.productPriceFont, true);
      this.updateFontStyle('productDesFontStyle', nextProps.productDesFont, true);
    }

    if (nextProps.i18n.locale !== this.props.i18n.locale) {
      const textTransform = buildOptionList(TEXT_TRANSFORM, nextProps.i18n);
      this.setState({ textTransform });
    }
  }

  render() {
    const {
      i18n,
      updateField,
      loading,
      fontFamily,
      productTitleColor,
      productTitleSize,
      productVendorColor,
      productVendorSize,
      productPriceColor,
      productOldPriceColor,
      productSalePriceColor,
      productPriceSize,
      productDesColor,
      productDesSize
    } = this.props;
    const {
      showProductTitleSection,
      showProductVendorSection,
      showProductPriceSection,
      showProductDescriptionSection,
      textTransform,
      productTitleFontStyle,
      productVendorFontStyle,
      productPriceFontStyle,
      productDesFontStyle
    } = this.state;

    return (
      <FormLayout>
        <TextStyle variation="subdued">
          {i18n.translate('AdminHTML.DefaultThemeSettings.DetailTab.helperText', {
            t1: (
              <Link external url={`${URL['DefaultThemeSettingsDoc']}#a2.2`}>
                {i18n.translate('AdminHTML.DefaultThemeSettings.DetailTab.helperText_t1')}
              </Link>
            )
          })}
        </TextStyle>
        {/** Product title */}
        <div className="groupFields-label">
          <Button
            disclosure={showProductTitleSection ? 'down' : 'up'}
            plain
            monochrome
            removeUnderline
            onClick={() => this.toggleState('showProductTitleSection')}>
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.DefaultThemeSettings.DetailTab.gFProductTitle_Label')}
            </TextStyle>
          </Button>
        </div>
        {showProductTitleSection && (
          <div className="groupFields-content">
            <div style={{ marginTop: '-2rem' }}>
              <Stack vertical>
                <div className="groupFields-flex-2">
                  <RenderColorPickerHexPopover
                    colorValue={productTitleColor}
                    fieldName="products.productTitleColor"
                    fieldLabel={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.DetailTab.field1_Label'
                    )}
                    updateField={updateField}
                  />

                  <Field
                    name="products.productTitleFont"
                    component={RenderSelect2}
                    options={fontFamily}
                    label={i18n.translate('AdminHTML.DefaultThemeSettings.DetailTab.field2_Label')}
                    placeholder={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.Constants.fontSelect_Placeholder'
                    )}
                    placeholderEmpty={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.Constants.noFont_Placeholder'
                    )}
                    onChange={value => this.updateFontStyle('productTitleFontStyle', value)}
                  />
                </div>
                <div className="groupFields-flex-2">
                  <div>
                    <Field
                      name="products.productTitleTransform"
                      component={RenderSelect}
                      label={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.DetailTab.field4_Label'
                      )}
                      options={textTransform}
                    />
                  </div>
                  <div>
                    <Field
                      name="products.productTitleFontStyle"
                      component={RenderSelect}
                      label={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.DetailTab.field2_1_Label'
                      )}
                      options={productTitleFontStyle}
                    />
                  </div>
                </div>
                <div className="groupFields-flex-2">
                  <div>
                    <p className="Polaris-Label">
                      <label style={{ display: 'inline' }} className="Polaris-Label__Text">
                        {i18n.translate('AdminHTML.DefaultThemeSettings.DetailTab.field3_Label')}
                      </label>
                    </p>
                    {loading ? (
                      <div style={{ marginTop: '1rem' }}>
                        <SkeletonDisplayText size="small" />
                      </div>
                    ) : (
                      <div className="range-slider-input">
                        <Field
                          name="products.productTitleSize"
                          component={RenderTextField}
                          onBlur={(event, value) =>
                            this.onBlurTextField(event, value, 'productTitleSize')
                          }
                          normalize={(value, prevValue) =>
                            normalizeNumberRangeSlider(value, prevValue, 30)
                          }
                          validate={[minValue(10)]}
                          anotherProps={{
                            connectedLeft: (
                              <RangeSlider
                                value={productTitleSize}
                                onChange={value => updateField('products.productTitleSize', value)}
                                max={30}
                                min={10}
                              />
                            )
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Stack>
            </div>
          </div>
        )}
        <div className="breakLine" />

        {/** Product vendor */}
        <div className="groupFields-label">
          <Button
            disclosure={showProductVendorSection ? 'down' : 'up'}
            plain
            monochrome
            removeUnderline
            onClick={() => this.toggleState('showProductVendorSection')}>
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.DefaultThemeSettings.DetailTab.gFProductVendor_Label')}
            </TextStyle>
          </Button>
        </div>
        {showProductVendorSection && (
          <div className="groupFields-content">
            <div style={{ marginTop: '-2rem' }}>
              <Stack vertical>
                <div className="groupFields-flex-2">
                  <RenderColorPickerHexPopover
                    colorValue={productVendorColor}
                    fieldName="products.productVendorColor"
                    fieldLabel={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.DetailTab.field5_Label'
                    )}
                    updateField={updateField}
                  />

                  <Field
                    name="products.productVendorFont"
                    component={RenderSelect2}
                    options={fontFamily}
                    label={i18n.translate('AdminHTML.DefaultThemeSettings.DetailTab.field6_Label')}
                    placeholder={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.Constants.fontSelect_Placeholder'
                    )}
                    placeholderEmpty={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.Constants.noFont_Placeholder'
                    )}
                    onChange={value => this.updateFontStyle('productVendorFontStyle', value)}
                  />
                </div>
                <div className="groupFields-flex-2">
                  <Field
                    name="products.productVendorTransform"
                    component={RenderSelect}
                    label={i18n.translate('AdminHTML.DefaultThemeSettings.DetailTab.field8_Label')}
                    options={textTransform}
                  />
                  <Field
                    name="products.productVendorFontStyle"
                    component={RenderSelect}
                    label={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.DetailTab.field6_1_Label'
                    )}
                    options={productVendorFontStyle}
                  />
                </div>
                <div className="groupFields-flex-2">
                  <div>
                    <p className="Polaris-Label">
                      <label style={{ display: 'inline' }} className="Polaris-Label__Text">
                        {i18n.translate('AdminHTML.DefaultThemeSettings.DetailTab.field7_Label')}
                      </label>
                    </p>
                    {loading ? (
                      <div style={{ marginTop: '1rem' }}>
                        <SkeletonDisplayText size="small" />
                      </div>
                    ) : (
                      <div className="range-slider-input">
                        <Field
                          name="products.productVendorSize"
                          component={RenderTextField}
                          onBlur={(event, value) =>
                            this.onBlurTextField(event, value, 'productVendorSize')
                          }
                          normalize={(value, prevValue) =>
                            normalizeNumberRangeSlider(value, prevValue, 30)
                          }
                          validate={[minValue(10)]}
                          anotherProps={{
                            connectedLeft: (
                              <RangeSlider
                                value={productVendorSize}
                                onChange={value => updateField('products.productVendorSize', value)}
                                max={30}
                                min={10}
                              />
                            )
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Stack>
            </div>
          </div>
        )}

        <div className="breakLine" />
        {/** Product price */}
        <div className="groupFields-label">
          <Button
            disclosure={showProductPriceSection ? 'down' : 'up'}
            plain
            monochrome
            removeUnderline
            onClick={() => this.toggleState('showProductPriceSection')}>
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.DefaultThemeSettings.DetailTab.gFProductPrice_Label')}
            </TextStyle>
          </Button>
        </div>

        {showProductPriceSection && (
          <div className="groupFields-content">
            <div style={{ marginTop: '-2rem' }}>
              <Stack vertical>
                <div className="groupFields-flex-2">
                  <RenderColorPickerHexPopover
                    colorValue={productPriceColor}
                    fieldName="products.productPriceColor"
                    fieldLabel={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.DetailTab.field9_Label'
                    )}
                    updateField={updateField}
                  />

                  <RenderColorPickerHexPopover
                    colorValue={productOldPriceColor}
                    fieldName="products.productOldPriceColor"
                    fieldLabel={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.DetailTab.field10_Label'
                    )}
                    updateField={updateField}
                  />
                </div>
                <div className="groupFields-flex-2">
                  <RenderColorPickerHexPopover
                    colorValue={productSalePriceColor}
                    fieldName="products.productSalePriceColor"
                    fieldLabel={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.DetailTab.field11_Label'
                    )}
                    updateField={updateField}
                  />

                  <Field
                    name="products.productPriceFont"
                    component={RenderSelect2}
                    options={fontFamily}
                    label={i18n.translate('AdminHTML.DefaultThemeSettings.DetailTab.field12_Label')}
                    placeholder={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.Constants.fontSelect_Placeholder'
                    )}
                    placeholderEmpty={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.Constants.noFont_Placeholder'
                    )}
                    onChange={value => this.updateFontStyle('productPriceFontStyle', value)}
                  />
                </div>
                <div className="groupFields-flex-2">
                  <div>
                    <p className="Polaris-Label">
                      <label style={{ display: 'inline' }} className="Polaris-Label__Text">
                        {i18n.translate('AdminHTML.DefaultThemeSettings.DetailTab.field13_Label')}
                      </label>
                    </p>
                    {loading ? (
                      <div style={{ marginTop: '1rem' }}>
                        <SkeletonDisplayText size="small" />
                      </div>
                    ) : (
                      <div className="range-slider-input">
                        <Field
                          name="products.productPriceSize"
                          component={RenderTextField}
                          onBlur={(event, value) =>
                            this.onBlurTextField(event, value, 'productPriceSize')
                          }
                          normalize={(value, prevValue) =>
                            normalizeNumberRangeSlider(value, prevValue, 30)
                          }
                          validate={[minValue(10)]}
                          anotherProps={{
                            connectedLeft: (
                              <RangeSlider
                                value={productPriceSize}
                                onChange={value => updateField('products.productPriceSize', value)}
                                max={30}
                                min={10}
                              />
                            )
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <Field
                    name="products.productPriceFontStyle"
                    component={RenderSelect}
                    label={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.DetailTab.field12_1_Label'
                    )}
                    options={productPriceFontStyle}
                  />
                </div>
              </Stack>
            </div>
          </div>
        )}

        <div className="breakLine" />
        {/** Product description */}
        <div className="groupFields-label">
          <Button
            disclosure={showProductDescriptionSection ? 'down' : 'up'}
            plain
            monochrome
            removeUnderline
            onClick={() => this.toggleState('showProductDescriptionSection')}>
            <TextStyle variation="strong">
              {i18n.translate(
                'AdminHTML.DefaultThemeSettings.DetailTab.gFProductDescription_Label'
              )}
            </TextStyle>
          </Button>
        </div>

        {showProductDescriptionSection && (
          <div className="groupFields-content">
            <div style={{ marginTop: '-2rem' }}>
              <Stack vertical>
                <div className="groupFields-flex-2">
                  <RenderColorPickerHexPopover
                    colorValue={productDesColor}
                    fieldName="products.productDesColor"
                    fieldLabel={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.DetailTab.field14_Label'
                    )}
                    updateField={updateField}
                  />

                  <Field
                    name="products.productDesFont"
                    component={RenderSelect2}
                    options={fontFamily}
                    label={i18n.translate('AdminHTML.DefaultThemeSettings.DetailTab.field15_Label')}
                    placeholder={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.Constants.fontSelect_Placeholder'
                    )}
                    placeholderEmpty={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.Constants.noFont_Placeholder'
                    )}
                    onChange={value => this.updateFontStyle('productDesFontStyle', value)}
                  />
                </div>
                <div className="groupFields-flex-2">
                  <div>
                    <p className="Polaris-Label">
                      <label style={{ display: 'inline' }} className="Polaris-Label__Text">
                        {i18n.translate('AdminHTML.DefaultThemeSettings.DetailTab.field16_Label')}
                      </label>
                    </p>
                    {loading ? (
                      <div style={{ marginTop: '1rem' }}>
                        <SkeletonDisplayText size="small" />
                      </div>
                    ) : (
                      <div className="range-slider-input">
                        <Field
                          name="products.productDesSize"
                          component={RenderTextField}
                          onBlur={(event, value) =>
                            this.onBlurTextField(event, value, 'productDesSize')
                          }
                          normalize={(value, prevValue) =>
                            normalizeNumberRangeSlider(value, prevValue, 30)
                          }
                          validate={[minValue(10)]}
                          anotherProps={{
                            connectedLeft: (
                              <RangeSlider
                                value={productDesSize}
                                onChange={value => updateField('products.productDesSize', value)}
                                max={30}
                                min={10}
                              />
                            )
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <Field
                    name="products.productDesFontStyle"
                    component={RenderSelect}
                    label={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.DetailTab.field15_1_Label'
                    )}
                    options={productDesFontStyle}
                  />
                </div>
              </Stack>
            </div>
          </div>
        )}
      </FormLayout>
    );
  }

  toggleState = (fieldName: string) => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  // Handle blur input
  onBlurTextField = (event, value, name) => {
    const { updateField } = this.props;
    if (!value) {
      event.preventDefault();
      updateField(name, '0');
    }
  };

  // Handle change product title font
  updateFontStyle = (field, selected, firstLoad = false) => {
    if (!selected) return;

    const { fontList, updateField } = this.props;
    const font = _.find(fontList, font => {
      // $FlowFixMe
      return font.family === selected.value;
    });

    if (!font) return;
    const variants = _.get(font, 'variants', []);

    const fontStyles = variants.map(variant => ({ label: variant, value: variant }));

    this.setState({ [field]: fontStyles });

    // Auto select first item if not first loading
    if (!firstLoad) {
      updateField(`products.${field}`, fontStyles[0].value);
    }
  };
}

const mapStateToProps = (state, props) => {
  const defaultThemeSettingsForm = getFormValues('DefaultThemeSettingsForm')(state);
  const { defaultThemeSettings } = state;

  let colorList = {};
  [
    'productTitleColor',
    'productVendorColor',
    'productPriceColor',
    'productOldPriceColor',
    'productSalePriceColor',
    'productDesColor'
  ].map(item => {
    colorList[item] = _.get(defaultThemeSettingsForm, `products.${item}`, '#FFFFFF');
    return '';
  });

  return {
    ...colorList,
    fontList: _.get(state, 'font.fontList', []),
    fontFamily: _.get(state, 'font.fontFamily', []),
    fontListSuccess: _.get(state, 'font.fontListSuccess', false),
    loading: _.get(defaultThemeSettings, 'loadingDefaultThemeSettings', false),
    productTitleFont: _.get(defaultThemeSettingsForm, 'products.productTitleFont', {}),
    productTitleSize: _.get(defaultThemeSettingsForm, 'products.productTitleSize', 0),
    productVendorFont: _.get(defaultThemeSettingsForm, 'products.productVendorFont', {}),
    productVendorSize: _.get(defaultThemeSettingsForm, 'products.productVendorSize', 0),
    productPriceFont: _.get(defaultThemeSettingsForm, 'products.productPriceFont', {}),
    productPriceSize: _.get(defaultThemeSettingsForm, 'products.productPriceSize', 0),
    productDesFont: _.get(defaultThemeSettingsForm, 'products.productDesFont', {}),
    productDesSize: _.get(defaultThemeSettingsForm, 'products.productDesSize', 0)
  };
};

const mapDispatchToProps = dispatch => ({
  updateField: (id, data) => dispatch(change(`DefaultThemeSettingsForm`, id, data))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(DetailTab));
